import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useLazyDownloadFileQuery,
  useLazyGetDownloadLogQuery,
  useLazyGetProjectByIdQuery,
  useSendToFTPMutation,
} from "../../api/projectsApi";
import Button, { ButtonModeE } from "../../components/Button/Button";
import { H1 } from "../../components/Text/Text";
import { MarkNotSuitableForBroadcastingPopupContent } from "../../popups/MarkNotSuitableForBroadcasting/MarkNotSuitableForBroadcastingFooter";
import SetHouseNumberBody from "../../popups/SetHouseNumber/SetHouseNumberBody";
import SetHouseNumberFooter from "../../popups/SetHouseNumber/SetHouseNumberFooter";
import { useAppSelector } from "../../store/hooks";
import { selectShareToken } from "../../store/selectors/appSelectors";
import { TOAST_DURATION, TOAST_DURATION_LONG } from "../helpers/constants";
import usePopup from "./usePopup";
import { useTranslate } from "./useTranslate";

import styles from "../../pages/projects/components/VideoControlButtons/styles.module.scss";

// spec-49 — the version-level action handlers that used to live inline in VideoControlButtons (download,
// send to FTP, set house number, mark not suitable). Extracted, not rewritten, so the delivery page and the
// versions library run the same code. The version shape is the subset both callers have.
export type VersionActionsSourceT = {
  Id: number;
  AdId: string;
  Name: string;
  HouseNumber?: string | null;
  sponsershipHouseNumber?: string | null;
};

type OptionsT = {
  /** The delivery the version belongs to. Optional: the delivery page reads it from the route when absent. */
  projectId?: number | string;
  /** Re-load the delivery after an action that changes it (house number, not-suitable). */
  refetchProject?: () => void;
};

const ALREADY_UPLOADED_ERROR_TEXT = "AlreadyUploading";

function downloadFileURL(link: string): void {
  const anchorElement = document.createElement("a");
  anchorElement.href = link;
  anchorElement.setAttribute("download", "");
  // spec-49 CORRECTIONS-01 Task 8 — open in a new tab, so that when the browser navigates to the URL
  // instead of saving it (a content type it can display, or an error page from the storage bucket) the
  // user does not lose the page they were on. ⚠️ Shared with the delivery page through
  // VideoControlButtons; the change lands there too, which is intended.
  anchorElement.target = "_blank";
  anchorElement.style.display = "none";
  document.body.appendChild(anchorElement);
  anchorElement.click();
  document.body.removeChild(anchorElement);
}

export const useVersionActions = (version: VersionActionsSourceT, options: OptionsT = {}) => {
  const { Id, AdId, Name, HouseNumber, sponsershipHouseNumber } = version;
  const { refetchProject = () => {} } = options;
  const shareToken = useAppSelector(selectShareToken);
  const { createPopup, removePopup, removeAllPopups } = usePopup();
  const { t } = useTranslate();
  const { projectId: routeProjectId } = useParams();
  const projectDeliveryId = options.projectId ?? routeProjectId;

  const [downloadFile, { isFetching: isDownloading }] = useLazyDownloadFileQuery();
  const [updateDownloadLogs] = useLazyGetDownloadLogQuery();
  const [updateProjectData] = useLazyGetProjectByIdQuery();
  const [sendToFTP] = useSendToFTPMutation();

  const handleDownload = async (asSponsorship: boolean) => {
    const { data }: any = await downloadFile({
      id: Id,
      shareToken,
    });

    downloadFileURL(data);

    if (projectDeliveryId) {
      setTimeout(() => {
        updateDownloadLogs({ projectId: projectDeliveryId });
        updateProjectData({ id: projectDeliveryId });
      }, 2000);
    }
  };

  const confirmSendToFTP = (
    asSponsorship: boolean,
    confirmed: boolean,
    houseNumberProp?: string,
  ) => {
    const handleSendToFTP = async (asSponsorship, confirmed) => {
      const res: any = await sendToFTP({ Id, asSponsorship, confirmed });
      if (res?.error?.data === ALREADY_UPLOADED_ERROR_TEXT) {
        handleSendToFTP(asSponsorship, true);
      } else if (res?.error) {
        res?.error?.status === 401 &&
          toast.error(t`Unauthorized request`, {
            autoClose: TOAST_DURATION,
          });
        removePopup();
        return;
        // } else if (res?.error?.data && res?.error?.data !== ALREADY_UPLOADED_ERROR_TEXT) {
        //     toast(res?.error?.data, {
        //         type: 'error',
        //         autoClose: TOAST_DURATION_LONG,
        //     });
        //     return;
      } else {
        toast(
          t(
            `The video for the version ${Name} - ${
              houseNumberProp ||
              (asSponsorship ? sponsershipHouseNumber : HouseNumber)
            } sent to your FTP server.`,
          ),
          {
            type: "success",
            autoClose: TOAST_DURATION_LONG,
          },
        );
        removeAllPopups();
      }
    };

    createPopup({
      header: <H1>Send to FTP</H1>,
      body: (
        <div>
          <p
            className={styles.popupTitle}
            dangerouslySetInnerHTML={{
              __html: t`Do you wish to send the video for version '${Name}' - ${
                houseNumberProp ||
                (asSponsorship ? sponsershipHouseNumber : HouseNumber)
              } to the FTP server?`,
            }}
          ></p>
        </div>
      ),
      footer: (
        <div className={styles.footerButtonsWrapper}>
          <Button onClick={removePopup} mode={ButtonModeE.OUTLINED}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSendToFTP(asSponsorship, confirmed)}
            useGlobalLoading
          >
            Send
          </Button>
        </div>
      ),
      className: styles.sendToFTPPopupWrapper,
    });
  };

  const openMarkNotSuitableForBroadcasting = () => {
    createPopup({
      header: <H1>Not suitable for broadcasting</H1>,
      body: (
        <MarkNotSuitableForBroadcastingPopupContent
          projectId={projectDeliveryId ? +projectDeliveryId : undefined}
          versionAdId={AdId}
          refetchProject={refetchProject}
        />
      ),
      className: styles.popupWrapper,
    });
  };

  const openSetHouseNumber = () => {
    createPopup({
      header: <H1>Set house number</H1>,
      body: (
        <SetHouseNumberBody
          AdId={AdId}
          projectId={+projectDeliveryId}
        />
      ),
      footer: (
        <SetHouseNumberFooter
          confirmSendToFTP={confirmSendToFTP}
          versionId={Id}
          projectId={projectDeliveryId ? +projectDeliveryId : undefined}
        />
      ),
      className: styles.popupWrapper,
    });
  };

  return {
    handleDownload,
    isDownloading,
    confirmSendToFTP,
    openMarkNotSuitableForBroadcasting,
    openSetHouseNumber,
    projectDeliveryId,
  };
};
